import React, { useEffect, useState } from 'react';
import { useCredits } from '../../contexts/CreditContext';
import { useToast } from '../../contexts/ToastContext';

export const CreditPurchase: React.FC = () => {
  const { availableProducts, fetchAvailableProducts, purchaseCredits } = useCredits();
  const { showError, showSuccess } = useToast();
  const [purchasingProduct, setPurchasingProduct] = useState<string | null>(null);

  useEffect(() => {
    fetchAvailableProducts();
  }, [fetchAvailableProducts]);

  const handlePurchase = async (productType: string) => {
    setPurchasingProduct(productType);
    try {
      const result = await purchaseCredits(productType);
      if (result.success) {
        showSuccess('Redirecting to checkout...');
        // The purchaseCredits function will redirect to Stripe checkout
      } else {
        showError(result.error || 'Failed to initiate purchase');
      }
    } catch (error) {
      showError('Failed to purchase credits');
    } finally {
      setPurchasingProduct(null);
    }
  };

  // Map product names to product types for the backend (fallback)
  const getProductType = (productName: string): string => {
    switch (productName.toLowerCase()) {
      case 'quick charm':
        return 'quick_charm';
      case 'focus spell':
        return 'focus_spell';
      case 'wizard mode':
        return 'wizard_mode';
      default:
        // Fallback: try to extract from product name
        return productName.toLowerCase().replace(/\s+/g, '_');
    }
  };

  if (availableProducts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="loading loading-spinner loading-lg mb-4"></div>
        <p className="text-base-content/60">Loading credit packages...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-base-content mb-2">Choose Your Credit Package</h3>
        <p className="text-base-content/70">Select the package that best fits your needs</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {availableProducts.map((product) => {
          const productType = product.productType || getProductType(product.name);
          const isPurchasing = purchasingProduct === productType;
          
          return (
            <div key={product.productId} className="relative group">
              <div className="bg-base-100 border-2 border-base-300 rounded-xl p-6 hover:border-primary hover:shadow-lg transition-all duration-200 h-full">
                {/* Popular badge for middle option */}
                {product.credits === 250 && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-content text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h4 className="text-lg font-bold text-base-content mb-2">{product.name}</h4>
                  <p className="text-sm text-base-content/70 mb-4">{product.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-primary">{product.displayPrice}</span>
                  </div>
                  
                  <div className="bg-base-200 rounded-lg p-3 mb-6">
                    <div className="text-2xl font-bold text-base-content">{product.credits}</div>
                    <div className="text-sm text-base-content/70">credits</div>
                    <div className="text-xs text-base-content/60 mt-1">
                      ${(product.price / 100 / product.credits).toFixed(3)} per credit
                    </div>
                  </div>
                </div>
                
                <button
                  className={`w-full btn btn-primary ${isPurchasing ? 'loading' : ''}`}
                  onClick={() => handlePurchase(productType)}
                  disabled={purchasingProduct !== null}
                >
                  {isPurchasing ? 'Processing...' : 'Buy Now'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-8 bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-primary mt-0.5">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-base-content mb-1">How Credits Work</h4>
            <p className="text-sm text-base-content/80">
              Each resume analysis costs 1 credit. Credits never expire and can be used anytime. 
              No subscriptions - only pay for what you need!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 